import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { useSupabase } from '@/hooks/useSupabase'
import { showToast } from '@/components/layout/Toast'
import Modal from '@/components/layout/Modal'
import type { Cotizacion } from '@/lib/types'
import { formatCurrency, formatDate } from '@/lib/utils'

const estadoBadge: Record<string, string> = {
  borrador: 'badge-borrador', enviada: 'badge-enviada', aceptada: 'badge-aceptada', rechazada: 'badge-rechazada',
}

export default function Cotizaciones() {
  const db = useSupabase()
  const [items, setItems] = useState<Cotizacion[]>([])
  const [loading, setLoading] = useState(true)
  const [del, setDel] = useState<Cotizacion | null>(null)
  const [search, setSearch] = useState('')

  const load = async () => { try { setItems(await db.getAll<Cotizacion>('cotizaciones')) } catch { showToast('Error al cargar', 'error') } finally { setLoading(false) } }
  useEffect(() => { load() }, [])

  const doDelete = async () => { if (!del) return; try { await db.remove('cotizaciones', del.id); showToast('Eliminada', 'success'); setDel(null); await load() } catch { showToast('Error', 'error') } }

  const filtered = items.filter(c =>
    [c.numero, c.cliente_nombre, c.proyecto_nombre].some(v => v?.toLowerCase().includes(search.toLowerCase()))
  )

  if (loading) return <div className="page-enter" style={{ padding: '2rem' }}><div className="page-header"><h1>Cotizaciones</h1></div><div style={{ color: 'var(--text-secondary)', marginTop: '2rem', textAlign: 'center' }}><div className="auth-spinner" style={{ margin: '0 auto 12px' }} />Cargando...</div></div>

  return (
    <div className="page-enter">
      <div className="page-header">
        <div><h1>Cotizaciones</h1><p className="page-subtitle">{items.length} cotización{items.length !== 1 ? 'es' : ''}</p></div>
        <Link to="/cotizaciones/nueva" className="btn btn-primary">+ Nueva Cotización</Link>
      </div>

      {items.length > 3 && (
        <div style={{ marginBottom: 16 }}><input className="form-input" placeholder="Buscar por N°, cliente o proyecto..." value={search} onChange={e => setSearch(e.target.value)} style={{ maxWidth: 400 }} /></div>
      )}

      {items.length === 0 ? (
        <div className="empty-state"><h3>Sin cotizaciones</h3><p>Crea tu primera cotización.</p><Link to="/cotizaciones/nueva" className="btn btn-primary">Crear Cotización</Link></div>
      ) : (
        <div className="table-container"><table><thead><tr><th>N°</th><th>Cliente</th><th>Proyecto</th><th>Fecha</th><th>Total</th><th>Estado</th><th style={{ width: 150 }}>Acciones</th></tr></thead>
          <tbody>{filtered.map(c => (
            <tr key={c.id}>
              <td style={{ fontWeight: 700, fontFamily: 'monospace' }}>{c.numero}</td>
              <td>{c.cliente_nombre || '—'}</td>
              <td style={{ maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.proyecto_nombre || '—'}</td>
              <td>{formatDate(c.fecha_emision)}</td>
              <td style={{ fontWeight: 700 }}>{formatCurrency(c.total, c.moneda)}</td>
              <td><span className={`badge ${estadoBadge[c.estado] || ''}`}>{c.estado}</span></td>
              <td>
                <div style={{ display: 'flex', gap: 6 }}>
                  <Link to={`/cotizaciones/${c.id}`} className="btn btn-sm btn-secondary" title="Ver">👁️</Link>
                  <Link to={`/cotizaciones/${c.id}/editar`} className="btn btn-sm btn-secondary" title="Editar">✏️</Link>
                  <button className="btn btn-sm btn-danger" onClick={() => setDel(c)} title="Eliminar">🗑️</button>
                </div>
              </td>
            </tr>
          ))}</tbody></table></div>
      )}

      <Modal isOpen={!!del} onClose={() => setDel(null)} title="Eliminar Cotización">
        <p>¿Eliminar cotización <strong>N° {del?.numero}</strong>?</p>
        <div className="modal-actions"><button className="btn btn-secondary" onClick={() => setDel(null)}>Cancelar</button><button className="btn btn-danger" onClick={doDelete}>Eliminar</button></div>
      </Modal>
    </div>
  )
}
